import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Socket } from 'socket.io-client';

import { CONTENTS_SHARED_WITH_ME, ORGANIZATION, VIDEO_TYPE, VIDEOFEED } from '../../enums/TYPES';
import { ExtendedVIDEOFEED } from '../../hoc/Layout/Sidebar/SiderbarModals/UploadContentModal';
import { TSidebarMenu } from '../../hoc/Layout/Sidebar/SIDEBAR_MENU';
import { STATUS, TRANSCODING_STATUS } from '../../enums/ENUMS';
import { TClip } from '../../components/Timeline/TimelineClip';
import { SortOptions, SortType } from '../../enums/SORT';

type RolesById = {
  [id: string]: {
    id: string;
    title: string;
    description: string;
  };
};

export enum UploadingStatus {
  ADDED = 'added',
  UPLOADING = 'uploading',
  TRANSCODING = 'processing',
  FINISHED = 'finished',
  FAILED = 'failed',
}

export type UploadContentState = {
  id: string;
  videofeedId: string;
  videofeedTitle: string;
  status: UploadingStatus;
  progress?: string;
  size: number;
  name: string;
  originalName: string;
  videos?: VIDEO_TYPE[];
  uploadMode?: 'package' | 'append' | 'separate' | 'additional-angle';
  baseVideoCount?: number;
  mergeSessionId?: string;
  videoId?: string;
  createdAt?: number;
  uploadError?: string;
};

export const ADDITIONAL_ANGLE_PLACEHOLDER_TTL = 24 * 60 * 60 * 1000;
const ADDITIONAL_ANGLE_PLACEHOLDERS_KEY = 'additional-angle-placeholders';

const filterAdditionalAnglePlaceholders = (items: UploadContentState[]) => {
  const now = Date.now();
  return items.filter(
    (item) =>
      item?.uploadMode === 'additional-angle' &&
      item?.status !== UploadingStatus.FAILED &&
      typeof item?.createdAt === 'number' &&
      now - item.createdAt < ADDITIONAL_ANGLE_PLACEHOLDER_TTL,
  );
};

const getAdditionalAnglePlaceholders = (): UploadContentState[] => {
  if (typeof window === 'undefined') return [];

  try {
    const saved = JSON.parse(
      window.localStorage.getItem(ADDITIONAL_ANGLE_PLACEHOLDERS_KEY) || '[]',
    );
    if (!Array.isArray(saved)) return [];

    const placeholders = filterAdditionalAnglePlaceholders(saved);
    if (placeholders.length !== saved.length) {
      if (placeholders.length) {
        window.localStorage.setItem(
          ADDITIONAL_ANGLE_PLACEHOLDERS_KEY,
          JSON.stringify(placeholders),
        );
      } else {
        window.localStorage.removeItem(ADDITIONAL_ANGLE_PLACEHOLDERS_KEY);
      }
    }
    return placeholders;
  } catch {
    return [];
  }
};

const persistAdditionalAnglePlaceholders = (items: UploadContentState[]) => {
  if (typeof window === 'undefined') return;

  try {
    const placeholders = filterAdditionalAnglePlaceholders(items);
    if (placeholders.length) {
      window.localStorage.setItem(
        ADDITIONAL_ANGLE_PLACEHOLDERS_KEY,
        JSON.stringify(placeholders),
      );
    } else {
      window.localStorage.removeItem(ADDITIONAL_ANGLE_PLACEHOLDERS_KEY);
    }
  } catch {}
};

type PathArray = Array<TSidebarMenu['path']>;

export type TSharedStoreState = {
  organization: ORGANIZATION | null;
  status: STATUS;
  roles: RolesById[] | null;
  uploadProgress: number;
  transcodingStatus: TRANSCODING_STATUS;
  contentTitle: string;
  content: VIDEOFEED | null;
  isSidebarHidden: boolean;
  showNotes: boolean;
  uploadSpeedInBytes: number;
  unReadCount: number; // this is unread notification count (for ring bell in header);
  notificationTrigger: number;
  notificationClick: boolean;
  uploadingContentState: UploadContentState[];
  showTooltipsForComposerResize: boolean;
  followPlayhead: boolean;
  triggerForRefetching: number;
  disableOptionInMenu: PathArray;
  currentVideoThatInFrame: VIDEO_TYPE | null;
  folderIdToUpload: string;
  secondarySelectedClips: TClip[];
  preserveSecondaryClipOrder: boolean;
  selectedVideoQuality: { quality: string; url: string } | null; // { quality: "1080"; url: "www.video.com/video.mp4" }
  socket: Socket | null;
  uploadingFiles: { keyStorage: string; data?: ExtendedVIDEOFEED }[];
  droppedFilesObject: string[];
  showSidebar: boolean;
  sharedContent: CONTENTS_SHARED_WITH_ME[] | VIDEOFEED[];
  triggerToScrollToClip: boolean;
  arrayOfCurrentUploads: { videofeedName: string; uploadedBytes: number }[];
  commonTrigger: number;
  isDraggingCursor: boolean;
  showSpeedOption: boolean;
  currentFeed: VIDEOFEED | null;
  additionalAngleUploadFeed: VIDEOFEED | null;
  pkgToDelete: VIDEOFEED | null;
  sortNameAndDirection: { sortOption: SortOptions; sort: SortType } | null;
  emptyComposerStackActionTrigger: number;
  showWarningModal: boolean;
  warningText: string;
  warningRedirect: boolean;
};

const initialState: TSharedStoreState = {
  organization: null,
  status: STATUS.IDLE,
  roles: null,
  uploadProgress: 0,
  transcodingStatus: TRANSCODING_STATUS.DEFAULT,
  contentTitle: '',
  content: null,
  isSidebarHidden: false,
  showNotes: true,
  uploadSpeedInBytes: 0,
  unReadCount: 0,
  notificationTrigger: 0,
  notificationClick: false,
  uploadingContentState: getAdditionalAnglePlaceholders(),
  showTooltipsForComposerResize: false,
  followPlayhead: true,
  triggerForRefetching: 0,
  disableOptionInMenu: [],
  currentVideoThatInFrame: null,
  folderIdToUpload: '',
  secondarySelectedClips: [],
  preserveSecondaryClipOrder: false,
  selectedVideoQuality: null,
  socket: null,
  uploadingFiles: [],
  droppedFilesObject: [],
  showSidebar: true,
  sharedContent: [],
  triggerToScrollToClip: false,
  arrayOfCurrentUploads: [],
  commonTrigger: 0,
  isDraggingCursor: false,
  showSpeedOption: false,
  currentFeed: null,
  additionalAngleUploadFeed: null,
  pkgToDelete: null,
  sortNameAndDirection: null,
  emptyComposerStackActionTrigger: 0,
  showWarningModal: false,
  warningText: '',
  warningRedirect: false,
};

export const sharedStoreSlice = createSlice({
  name: 'shared store slice',
  initialState,
  reducers: {
    emptyComposerStackAction: (state) => {
      state.emptyComposerStackActionTrigger = Date.now();
    },
    updateSortNameAndDirection: (state, action) => {
      state.sortNameAndDirection = action.payload;
    },
    updatePkgToDelete: (state, action) => {
      state.pkgToDelete = action.payload;
    },
    setShowSpeedOption: (state, action) => {
      state.showSpeedOption = action.payload;
    },
    updateCurrentFeed: (state, action) => {
      state.currentFeed = action.payload;
    },
    updateAdditionalAngleUploadFeed: (state, action: PayloadAction<VIDEOFEED | null>) => {
      if (!state.additionalAngleUploadFeed || !action.payload) {
        state.additionalAngleUploadFeed = action.payload;
      }
    },
    updateDraggingCursor: (state, action) => {
      state.isDraggingCursor = action.payload;
    },
    updateCommonTrigger: (state) => {
      state.commonTrigger = state.commonTrigger + 1;
    },
    updateArrayOfCurrentUploads: (state, action) => {
      state.arrayOfCurrentUploads = action.payload;
    },
    updateTriggerToScroll: (state) => {
      state.triggerToScrollToClip = !state.triggerToScrollToClip;
    },
    updateSharedContent: (state, action) => {
      state.sharedContent = action.payload;
    },
    updateShowSidebar: (state, action: PayloadAction<boolean>) => {
      state.showSidebar = action.payload;
    },
    updateDroppedFilesObject: (state, action) => {
      state.droppedFilesObject = action.payload;
    },
    updateUploadingFiles: (state, action) => {
      state.uploadingFiles = [...state.uploadingFiles, action.payload];
    },
    clearUploadingFiles: (state) => {
      state.uploadingFiles = [];
    },
    updateSocketState: (state, action) => {
      state.socket = action.payload;
    },
    updateSelectedVideoQuality: (state, action: PayloadAction<{ quality: string; url: string }>) => {
      // { quality: "1080"; url: "www.video.com/video.mp4" }
      if (state.selectedVideoQuality?.url !== action.payload.url) {
        state.selectedVideoQuality = action.payload;
      }
    },
    updateSecondaySelectedClips: (state, action) => {
      state.secondarySelectedClips = action.payload;
      state.preserveSecondaryClipOrder = false;
    },
    updateOrderedSecondarySelectedClips: (state, action) => {
      state.secondarySelectedClips = action.payload;
      state.preserveSecondaryClipOrder = true;
    },
    updateCurrentVideoThatInFrame: (state, action) => {
      state.currentVideoThatInFrame = action.payload;
    },
    updateDisableOptionInMenu: (state, action) => {
      state.disableOptionInMenu = action.payload;
    },
    triggerRefetch: (state) => {
      state.triggerForRefetching = Math.random();
    },
    toFollowPlayhead: (state, action) => {
      state.followPlayhead = action.payload;
    },
    updateShowTooltipsForComposerResize: (state, action) => {
      state.showTooltipsForComposerResize = action.payload;
    },
    updateNotificationTrigger: (state, action) => {
      state.notificationTrigger = action.payload;
    },
    updateNotificationClick: (state, action) => {
      state.notificationClick = action.payload;
    },
    loadOrganization: (state, action: PayloadAction<ORGANIZATION>) => {
      state.organization = action.payload;
    },
    saveRoles: (state, action) => {
      state.roles = action.payload;
    },
    updateUploadProgress: (state, action) => {
      state.uploadProgress = action.payload;
    },
    updateTranscodingStatus: (state, action) => {
      state.transcodingStatus = action.payload;
    },
    setContent: (state, action: PayloadAction<VIDEOFEED>) => {
      state.content = action.payload;
    },
    switchSidebar: (state, action) => {
      state.isSidebarHidden = action.payload;
    },
    updateShowNotes: (state, action) => {
      state.showNotes = action.payload;
    },
    updateUploadSpeed: (state, action) => {
      state.uploadSpeedInBytes = action.payload;
    },
    updateUnreadCount: (state, action) => {
      state.unReadCount = action.payload;
    },
    updateUploadingContentState: (state, action: { payload: UploadContentState[] }) => {
      state.uploadingContentState = action.payload;
      persistAdditionalAnglePlaceholders(action.payload);
    },
    updateFolderIdToUpload: (state, action) => {
      state.folderIdToUpload = action.payload;
    },
    updateShowWarningModal: (state, action) => {
      state.showWarningModal = action.payload.show;
      state.warningText = action.payload.warningText;
      state.warningRedirect = action.payload?.redirect;
    },
  },
});

export const {
  loadOrganization,
  saveRoles,
  updateUploadProgress,
  updateTranscodingStatus,
  setContent,
  switchSidebar,
  updateShowNotes,
  updateUploadSpeed,
  updateUnreadCount,
  updateNotificationTrigger,
  updateNotificationClick,
  updateUploadingContentState,
  updateShowTooltipsForComposerResize,
  toFollowPlayhead,
  triggerRefetch,
  updateDisableOptionInMenu,
  updateCurrentVideoThatInFrame,
  updateFolderIdToUpload,
  updateSecondaySelectedClips,
  updateOrderedSecondarySelectedClips,
  updateSelectedVideoQuality,
  updateSocketState,
  updateUploadingFiles,
  clearUploadingFiles,
  updateDroppedFilesObject,
  updateShowSidebar,
  updateSharedContent,
  updateTriggerToScroll,
  updateArrayOfCurrentUploads,
  updateCommonTrigger,
  updateDraggingCursor,
  setShowSpeedOption,
  updateCurrentFeed,
  updateAdditionalAngleUploadFeed,
  updatePkgToDelete,
  updateSortNameAndDirection,
  emptyComposerStackAction,
  updateShowWarningModal,
} = sharedStoreSlice.actions;

export default sharedStoreSlice.reducer;
